import { forwardRef, useImperativeHandle, useRef } from 'react';
import useModalState from '@sdk/components/ui/modal/useModalState';
import Modal from '@sdk/components/ui/modal/Modal';
import H3 from '@sdk/components/ui/typography/H3';
import Button from '@sdk/components/ui/buttons/Button';
import ProgressButton from '@sdk/components/ui/buttons/ProgressButton';
import gettext from '@sdk/lang/gettext';
import ModalErrorDisplay from '@sdk/components/ui/modal/components/modalErrorDisplay/ModalErrorDisplay';
import Text14 from '@sdk/components/ui/typography/Text14';
import Row from '@sdk/components/ui/spacing/Row';
import Column from '@sdk/components/ui/spacing/Column';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import { INPUT_HEIGHT } from '@sdk/components/forms/constants';
import { LocationType } from '@sdk/locations/types/LocationType';

import { EditPersonaCollectionGroup } from '../onboardingChecklist.types';
import {
  Container,
  CenterContentRow,
  ModalContent,
  FooterRow,
  ButtonContainer,
  ScrollableArea,
} from '../components/modalBase.style';

import LocationRow from './LocationRow';
import { useLocationsForm } from './hooks/useLocationsForm';

const SCROLLABLE_HEIGHT = 460;

type ForwardedAttrs = {
  open: () => void;
};

type Props = {
  locations: LocationType[];
  editPersonaCollectionGroup: EditPersonaCollectionGroup;
};

const LocationsModal = forwardRef<ForwardedAttrs, Props>(
  ({ locations, editPersonaCollectionGroup }, ref) => {
    const { isVisible, open, close } = useModalState();
    const config = useRef({ noDismiss: true }).current;
    const { locationsField, submit, dirty, reset, submitErrors } = useLocationsForm({
      locations,
      editPersonaCollectionGroup,
      close,
    });

    useImperativeHandle(ref, () => ({
      open,
    }));

    return (
      <Modal fixed config={config} isVisible={isVisible} onDismiss={close}>
        <Container>
          <ModalContent>
            <CenterContentRow pTop={20} pBottom={20} pLeft={15} pRight={15}>
              <H3>{gettext('Add location addresses')}</H3>
            </CenterContentRow>
            <Row pLeft={15} pRight={15}>
              <Text14 pBottom={20} themeColor={ThemeColorType.gray}>
                {gettext('For geofencing feature to work, specify address for your locations:')}
              </Text14>
            </Row>
            <ScrollableArea pLeft={15} maxHeight={SCROLLABLE_HEIGHT}>
              {locationsField.map((location, index) => (
                <Column key={location.id.value} pRight={10}>
                  <LocationRow location={location} />
                  {locationsField.length - 1 !== index && (
                    <Column mBottom={20}>
                      <HorizontalSeparator />
                    </Column>
                  )}
                </Column>
              ))}
            </ScrollableArea>
          </ModalContent>

          <FooterRow mTop={20}>
            <ButtonContainer pRight={10}>
              <Button
                buttonType={ButtonType.default}
                onClick={() => {
                  reset();
                  close();
                }}>
                {gettext("I'll do it later")}
              </Button>
            </ButtonContainer>

            <ButtonContainer>
              <ProgressButton
                disabled={!dirty}
                buttonType={ButtonType.primary}
                onClick={submit}
                height={INPUT_HEIGHT}>
                {gettext('Save')}
              </ProgressButton>
            </ButtonContainer>
          </FooterRow>
        </Container>
        <ModalErrorDisplay error={submitErrors?.length ? submitErrors[0].message : ''} />
      </Modal>
    );
  }
);

LocationsModal.displayName = 'LocationsModal';

export default LocationsModal;
